import {useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {
  Box,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  TextFieldProps,
  Typography,
  InputLabel,
} from '@mui/material';
import PaymentLinkModal from './PaymentLinkModal';
import {useCreateRefundPaymentMutation, useRefundReason} from './hooks';
import {useCreateInvoice} from '../InvoiceDetails/hooks';
import {usePaymentMethodTypesHook} from '../../Calendar/hooks/usePaymentMethodTypesHook';
import {useUpdatePaymentHook} from '../../Calendar/hooks/useUpdatePaymentHook';
import {TEXT_CONSTANTS} from '../InvoiceDetails/constants';
import {PAYMENT_DETAILS_STRINGS} from './constant';
import {IPaymentDetailsButtonsProps, PaymentDetails} from './types';
import {InsuranceClaim} from '$api/finance/insuranceClaims/insuranceClaimDetails';
import {ICreateInvoiceVariables} from '~/api/finance/invoice';

import {
  PaymentDetailsButtonsWrapperStyles,
  roundedTextFieldStyles,
  dialogBodyStyles,
  dialogTitleStyles,
  dialogNoteStyles,
  formFieldContainerStyles,
  formFieldLabelStyles,
  selectStyles,
  dialogActionsContainerStyles,
  currencyAdornmentStyles,
  refundDialogStyles,
} from './styles';
import DialogComponent from '~/components/DialogComponent';
import FrontdeskButton from '~/components/FrontdeskButton';
import queryClient from '~/lib/queryClient';
import useAuthUser from '~/hooks/useAuthUser';
import {UserRoleName} from '~/modules/admin/constant';
import {formatDate} from '~/utils/date';
import {getRoute, RouteSection} from '$utils/route';
import {RoutePath} from '$constants/routes';

const RoundedTextField = (props: TextFieldProps) => (
  <TextField fullWidth sx={roundedTextFieldStyles} {...props} />
);

const GenerateInvoiceButton = ({
  paymentId,
  payment,
  insuranceClaimId,
  insuranceClaim,
  context,
}: {
  paymentId?: string;
  payment?: PaymentDetails;
  insuranceClaimId?: string;
  insuranceClaim?: InsuranceClaim;
  context?: 'calendar' | 'finance';
}) => {
  const navigate = useNavigate();

  const {createInvoice, isDisabled} = useCreateInvoice({
    onCreateInvoiceSuccess: () => {
      if (paymentId) {
        let backRoute;
        if (context === 'calendar') {
          backRoute = getRoute(
            RouteSection.Calendar,
            `${RoutePath.staff.calendar.bookingDetails}/payment/${paymentId}`
          );
        } else {
          backRoute = getRoute(
            RouteSection.Finance,
            `${RoutePath.staff.finance.payments}/${paymentId}`
          );
        }

        navigate(
          getRoute(
            RouteSection.Finance,
            `${RoutePath.staff.finance.payments}/${paymentId}/invoice`
          ),
          {
            state: {
              backRoute,
            },
          }
        );
      } else if (insuranceClaimId) {
        navigate(
          getRoute(
            RouteSection.Finance,
            `${RoutePath.staff.finance.insuranceClaims}/${insuranceClaimId}/invoice`
          )
        );
      }
    },
  });

  const handleCreateInvoice = () => {
    const variables: ICreateInvoiceVariables = {};

    if (paymentId) {
      variables.payment_id = Number(paymentId);
    }

    if (insuranceClaimId) {
      variables.insurance_claim_id = Number(insuranceClaimId);
    }

    createInvoice(variables);
  };

  const data = payment || insuranceClaim;
  const hasItems = data?.items && data.items.length > 0;
  const invoiceIssuedAt =
    payment?.invoice_issued_at || insuranceClaim?.invoice_issued_at;
  const buttonText = invoiceIssuedAt
    ? `Invoice: Last Update ${formatDate(invoiceIssuedAt)}`
    : TEXT_CONSTANTS.GENERATE_INVOICE;

  return (
    <FrontdeskButton
      variant="secondary"
      disabled={isDisabled || !hasItems}
      onClick={handleCreateInvoice}
      title={buttonText}
      className="highlight-purple"
    />
  );
};

const RefundPaymentButton = ({
  paymentId,
  amount,
}: {
  paymentId?: string;
  amount: string;
}) => {
  const initialFormState = useMemo(
    () => ({
      refund_amount: amount || '',
      refund_reason: '',
      refund_notes: '',
    }),
    [amount]
  );

  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState(initialFormState);

  const {createRefundPayment, isPending} = useCreateRefundPaymentMutation(
    () => {
      handleModalClose();
      queryClient.invalidateQueries({queryKey: ['payment-details', paymentId]});
    }
  );

  const {data: refundReasons, isLoading: isLoadingRefundReasons} =
    useRefundReason();

  const handleChange =
    (field: keyof typeof form, transform?: (val: string) => string) =>
    (
      e:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | SelectChangeEvent<string>
    ) => {
      const value = transform ? transform(e.target.value) : e.target.value;
      setForm(prev => ({...prev, [field]: value}));
    };

  const handleRefund = () => {
    if (!form.refund_reason) return;

    createRefundPayment({
      payment_id: Number(paymentId),
      amount: form.refund_amount,
      reason: form.refund_reason,
      notes: form.refund_notes,
    });
  };

  const handleModalClose = () => {
    setIsOpen(false);
    setForm(initialFormState);
  };

  return (
    <>
      <FrontdeskButton
        title={TEXT_CONSTANTS.REFUND_PAYMENT}
        disabled={isPending}
        onClick={() => setIsOpen(true)}
      />

      <DialogComponent
        open={isOpen}
        handleClose={handleModalClose}
        sx={refundDialogStyles}
        dialogBody={
          <Box sx={dialogBodyStyles}>
            <Typography variant="h5" sx={dialogTitleStyles}>
              {PAYMENT_DETAILS_STRINGS.REFUND_CONFIRMATION_MSG}
            </Typography>
            <Typography sx={dialogNoteStyles}>
              {PAYMENT_DETAILS_STRINGS.REFUND_NOTE}
            </Typography>

            <Stack spacing={2}>
              <Box sx={formFieldContainerStyles}>
                <Typography sx={formFieldLabelStyles}>
                  {PAYMENT_DETAILS_STRINGS.AMOUNT_TO_REFUND}
                </Typography>
                <RoundedTextField
                  name="refund_amount"
                  placeholder={
                    PAYMENT_DETAILS_STRINGS.AMOUNT_TO_REFUND_PLACEHOLDER
                  }
                  value={form.refund_amount}
                  onChange={handleChange('refund_amount', v =>
                    v.replace(/[^0-9]/g, '')
                  )}
                  InputProps={{
                    startAdornment: (
                      <span className={currencyAdornmentStyles.className}>
                        £
                      </span>
                    ),
                  }}
                />
              </Box>

              <Box sx={formFieldContainerStyles}>
                <Typography sx={formFieldLabelStyles}>
                  {PAYMENT_DETAILS_STRINGS.SELECT_REASON}
                </Typography>
                <FormControl fullWidth>
                  <Select
                    id="refund_reason"
                    value={form.refund_reason}
                    onChange={handleChange('refund_reason')}
                    displayEmpty
                    sx={selectStyles}
                  >
                    {isLoadingRefundReasons && (
                      <MenuItem value="">
                        {PAYMENT_DETAILS_STRINGS.LOADING_REFUND_REASONS}
                      </MenuItem>
                    )}
                    {refundReasons?.data?.map(({id, name}) => (
                      <MenuItem key={id} value={name}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={formFieldContainerStyles}>
                <Typography sx={formFieldLabelStyles}>
                  {PAYMENT_DETAILS_STRINGS.NOTES}
                </Typography>
                <RoundedTextField
                  name="refund_notes"
                  placeholder={PAYMENT_DETAILS_STRINGS.NOTES_PLACEHOLDER}
                  value={form.refund_notes}
                  onChange={handleChange('refund_notes')}
                  multiline
                  maxRows={3}
                />
              </Box>
            </Stack>

            <Box sx={dialogActionsContainerStyles}>
              <FrontdeskButton
                variant="danger"
                title={PAYMENT_DETAILS_STRINGS.CANCEL}
                onClick={handleModalClose}
              />
              <FrontdeskButton
                disabled={!form.refund_reason}
                title={PAYMENT_DETAILS_STRINGS.REFUND}
                onClick={handleRefund}
              />
            </Box>
          </Box>
        }
      />
    </>
  );
};

const TakePaymentButton = ({
  paymentId,
  payment,
}: {
  paymentId?: string;
  payment?: PaymentDetails;
}) => {
  const [selectedPaymentMode, setSelectedPaymentMode] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [paymentLinkModalOpen, setPaymentLinkModalOpen] = useState(false);

  const {
    data: paymentModes,
    isLoading: isPaymentModesLoading,
    isError: isPaymentModesError,
  } = usePaymentMethodTypesHook();

  const updatePaymentMutation = useUpdatePaymentHook({
    onPaymentLinkSuccess: () => {
      setPaymentLinkModalOpen(false);
      setModalOpen(false);
    },
  });

  const handleTakePayment = () => {
    if (!selectedPaymentMode) return;

    updatePaymentMutation.mutate({
      payment_id: paymentId || '',
      payment_method_type: selectedPaymentMode,
    });

    setModalOpen(false);
  };

  const handlePaymentLinkSend = (data: {
    sendViaEmail: boolean;
    sendViaSms: boolean;
    email: string;
    phone: string;
  }) => {
    updatePaymentMutation.mutate({
      payment_id: paymentId || '',
      payment_method_type: selectedPaymentMode,
      email: data.email,
      phone: data.phone,
      send_via_email: data.sendViaEmail,
      send_via_sms: data.sendViaSms,
    });
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedPaymentMode('');
  };

  const handlePaymentLinkModalClose = () => {
    setPaymentLinkModalOpen(false);
    setSelectedPaymentMode('');
  };

  const handlePaymentMethodChange = (value: string) => {
    setSelectedPaymentMode(value);

    const selectedPaymentMethod = paymentModes?.data?.find(
      opt => opt.id === value
    );

    if (
      selectedPaymentMethod?.id === 'payment_link' ||
      selectedPaymentMethod?.name?.toLowerCase().includes('payment link')
    ) {
      setPaymentLinkModalOpen(true);
      setModalOpen(false);
    }
  };

  return (
    <>
      <FrontdeskButton
        title={PAYMENT_DETAILS_STRINGS.TAKE_PAYMENT}
        disabled={updatePaymentMutation.isPending}
        onClick={() => setModalOpen(true)}
      />

      <DialogComponent
        open={modalOpen}
        handleClose={handleModalClose}
        sx={refundDialogStyles}
        dialogBody={
          <Box sx={dialogBodyStyles}>
            <Typography variant="h5" sx={dialogTitleStyles}>
              {PAYMENT_DETAILS_STRINGS.CONFIRM_PAYMENT_TITLE}
            </Typography>
            <Typography sx={dialogNoteStyles}>
              {PAYMENT_DETAILS_STRINGS.CONFIRM_PAYMENT_MESSAGE}
            </Typography>

            <Stack spacing={2}>
              <Box sx={formFieldContainerStyles}>
                <Typography sx={formFieldLabelStyles}>
                  {PAYMENT_DETAILS_STRINGS.SELECT_PAYMENT_METHOD_LABEL}
                </Typography>
                <FormControl fullWidth>
                  <InputLabel id="payment-mode-label">
                    {PAYMENT_DETAILS_STRINGS.PAYMENT_LINK_LABEL}
                  </InputLabel>
                  <Select
                    labelId="payment-mode-label"
                    value={selectedPaymentMode}
                    label={PAYMENT_DETAILS_STRINGS.SELECT_PAYMENT_METHOD_LABEL}
                    onChange={e => handlePaymentMethodChange(e.target.value)}
                    disabled={isPaymentModesLoading || isPaymentModesError}
                    sx={selectStyles}
                  >
                    {isPaymentModesLoading && (
                      <MenuItem value="">
                        {PAYMENT_DETAILS_STRINGS.LOADING_PAYMENT_METHODS}
                      </MenuItem>
                    )}
                    {isPaymentModesError && (
                      <MenuItem value="">
                        {PAYMENT_DETAILS_STRINGS.ERROR_LOADING_PAYMENT_METHODS}
                      </MenuItem>
                    )}
                    {paymentModes?.data?.map(option => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {selectedPaymentMode && (
                <Typography sx={dialogNoteStyles}>
                  {PAYMENT_DETAILS_STRINGS.PAYMENT_METHOD_LABEL}:{' '}
                  <b>
                    {paymentModes?.data?.find(
                      opt => opt.id === selectedPaymentMode
                    )?.name || ''}
                  </b>
                </Typography>
              )}
            </Stack>

            <Box sx={dialogActionsContainerStyles}>
              <FrontdeskButton
                variant="danger"
                title={PAYMENT_DETAILS_STRINGS.CANCEL}
                onClick={handleModalClose}
              />
              <FrontdeskButton
                disabled={
                  !selectedPaymentMode || updatePaymentMutation.isPending
                }
                title={PAYMENT_DETAILS_STRINGS.CONFIRM}
                loading={updatePaymentMutation.isPending}
                onClick={handleTakePayment}
              />
            </Box>
          </Box>
        }
      />

      <PaymentLinkModal
        open={paymentLinkModalOpen}
        onClose={handlePaymentLinkModalClose}
        onSend={handlePaymentLinkSend}
        paymentLink={payment?.payment_link || ''}
        email={payment?.email}
        phone={payment?.phone}
        countryCode={payment?.country_code}
        isLoading={false}
      />
    </>
  );
};

export default function PaymentDetailsButtons({
  paymentId,
  payment,
  insuranceClaimId,
  insuranceClaim,
}: IPaymentDetailsButtonsProps) {
  const {roleName} = useAuthUser()!;
  const isFinanceRole = roleName === UserRoleName.Finance;

  const showRefundButton =
    isFinanceRole &&
    payment &&
    (payment?.status === 'paid' || payment?.status === 'refund_requested');

  const showTakePaymentButton =
    isFinanceRole &&
    payment &&
    payment?.status === 'unpaid' &&
    (payment?.for_outstanding_amount === true ||
      (payment?.items && payment.items.length > 0));

  return (
    <Box sx={PaymentDetailsButtonsWrapperStyles}>
      <GenerateInvoiceButton
        paymentId={paymentId}
        payment={payment}
        insuranceClaimId={insuranceClaimId}
        insuranceClaim={insuranceClaim}
        context="finance"
      />
      {showTakePaymentButton && (
        <TakePaymentButton paymentId={paymentId} payment={payment} />
      )}
      {showRefundButton && (
        <RefundPaymentButton
          paymentId={paymentId}
          amount={payment?.amount || ''}
        />
      )}
    </Box>
  );
}
